import type { Ssm5FreezeFrameReadout } from '@packages/domain/ssm5';

import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { GetFreezeFrames } from '@packages/domain/ssm5';
import { container } from '@web/services/container';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Ssm5FreezeFramesTarget } from '../helpers/ssm5-paths';

const [$getFreezeFrames] = container.resolveOperations(GetFreezeFrames);
const logger = getLogger('ssm5');

/** The freeze frame read and the way to retry a failure while the data is still stored. */
export type Ssm5FreezeFramesRead = {
    /** Read again after a failure that is not gone; does nothing in any other state. */
    retry: () => void;
    /** Where the read stands. */
    state: Ssm5FreezeFramesState;
};

/** Where the freeze frame read stands. */
export type Ssm5FreezeFramesState =
    | {
          /** Marks a read that failed. */
          status: 'failed';
          /** Whether the data is no longer stored. */
          isGone: boolean;
      }
    | {
          /** Marks a completed read. */
          status: 'loaded';
          /** What the read found. */
          readout: Ssm5FreezeFrameReadout;
      }
    | {
          /** Marks a read in flight. */
          status: 'loading';
      };

/** A freeze frame read that has settled. */
type SettledRead = Extract<Ssm5FreezeFramesState, Record<'status', 'failed' | 'loaded'>>;

/**
 * Read the freeze frame data once per session and DTC, and again on a retry after a failure that is not
 * gone. Each read replaces the previous state and is aborted when the target changes or on unmount.
 *
 * @param cloudConnectorId - the Cloud Connector ID of the session
 * @param target - the DTC whose data to read
 * @returns where the read stands and the retry
 */
export function useSsm5FreezeFrames(
    cloudConnectorId: string,
    { dtcCode, module }: Ssm5FreezeFramesTarget
): Ssm5FreezeFramesRead {
    const { abortSignalRef, signalAbort } = useAbortController();
    const [state, setState] = useState<Ssm5FreezeFramesState>({ status: 'loading' });
    const [readCount, setReadCount] = useState(0);
    const canRetryRef = useRef(false);

    useEffect(() => {
        const signal = abortSignalRef.current;

        canRetryRef.current = false;
        setState({ status: 'loading' });
        readFreezeFrames(cloudConnectorId, { dtcCode, module }, signal).then((settled) => {
            if (!signal.aborted) {
                canRetryRef.current = canRetry(settled);
                setState(settled);
            }
        });

        return signalAbort;
    }, [abortSignalRef, cloudConnectorId, dtcCode, module, readCount, signalAbort]);

    const retry = useCallback(() => {
        if (canRetryRef.current) {
            canRetryRef.current = false;
            setReadCount((count) => count + 1);
        }
    }, []);

    return { retry, state };
}

/**
 * Whether the settled read permits another attempt.
 *
 * @param settled - the settled read
 * @returns true when the data failed to load and is still stored
 */
function canRetry(settled: SettledRead): boolean {
    switch (settled.status) {
        case 'failed':
            return !settled.isGone;
        case 'loaded':
            return false;
    }
}

/**
 * Read the data, logging an unexpected throw unless the read was aborted.
 *
 * @param cloudConnectorId - the Cloud Connector ID of the session
 * @param target - the DTC whose data to read
 * @param signal - aborts the read
 * @returns the loaded data or whether it is gone
 */
async function readFreezeFrames(
    cloudConnectorId: string,
    { dtcCode, module }: Ssm5FreezeFramesTarget,
    signal: AbortSignal
): Promise<SettledRead> {
    try {
        const result = await $getFreezeFrames({ cloudConnectorId, dtcCode, module, signal });

        return result.success
            ? { readout: result.readout, status: 'loaded' }
            : { isGone: result.isGone, status: 'failed' };
    } catch (error: unknown) {
        if (!signal.aborted) {
            logger.error(
                `The SSM5 freeze frame read of [${dtcCode}] through [${cloudConnectorId}] failed before it settled`,
                { cloudConnectorId, dtcCode, error, module }
            );
        }

        return { isGone: false, status: 'failed' };
    }
}
